import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../services/api'; // 프로젝트의 apiClient를 사용합니다.
import useViewportCheck from '../../hooks/useViewportCheck'; 

import ReactECharts from 'echarts-for-react';
import {
    Typography,
    Card,
    Slider,
    Row,
    Col,
    Statistic,
    Table,
    Alert,
    Space,
    Button,
    Select,
    Divider,
    DatePicker,
    Tabs,
    Collapse // Collapse 컴포넌트 임포트 추가
} from 'antd';
// 아이콘 임포트 추가
import { SearchOutlined, ArrowRightOutlined, ClockCircleOutlined, CloseCircleOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import Loading from '../../components/Loading';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Text, Paragraph } = Typography;
const { Title } = ResponsiveTypography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse; // Collapse.Panel 사용을 위해 추가

// 자산 영문명을 한글명으로 변환하기 위한 객체
const ASSET_NAMES_KO = {
    'S&P500': 'S&P 500', 'NASDAQ100': '나스닥100', 'Russell2000': '러셀 2000',
    "Bitcoin": '비트코인', "Ethereum": '이더리움',
    'KOSPI': '코스피', 'Gold': '금', 'Silver': '은', 'Copper': '구리',
    'US01Y': '미국채 1년', 'US02Y': "미국채 2년", 'US05Y': '미국채 5년', 'US10Y': '미국채 10년',
    'My_Portfolio': '내 포트폴리오',
    '추천 포트폴리오': '추천 포트폴리오'
};

// 차트 색상 설정
const CHART_COLORS = {
    'S&P500': '#4A90E2', 'NASDAQ100': '#003F5C', 'Russell2000': '#7E57C2',
    'Gold': '#F5A623', 'Silver': '#BDC3C7',
    'US01Y': '#4f5b66', 'US05Y': '#34495e', 'US10Y': '#2c3e50', 'US30Y': '#1a237e',
    'My_Portfolio': '#E53935', 'Drawdown': 'rgba(255, 99, 132, 0.5)',
    '추천 포트폴리오': '#9C27B0'
};

const BacktestLazyPortfolioPage = () => {
    // --- State 정의 ---
    const [weights, setWeights] = useState({});
    const [totalWeight, setTotalWeight] = useState(0);
    const [selectedAssets, setSelectedAssets] = useState(['S&P500',  "Gold", 'US01Y','US10Y']);
    const [availableAssets, setAvailableAssets] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [backtestStartDate, setBacktestStartDate] = useState(null);
    const [backtestEndDate, setBacktestEndDate] = useState(null);
    const [minAvailableDate, setMinAvailableDate] = useState(null);
    const [maxAvailableDate, setMaxAvailableDate] = useState(null);
    const [selectedCagrPeriod, setSelectedCagrPeriod] = useState('5');
    const [dateAdjustmentWarning, setDateAdjustmentWarning] = useState(null); // 날짜 조정 안내 메시지 상태

    const isNarrow = useViewportCheck();
    const statisticTitleFontSize = isNarrow ? '0.8rem' : '0.9rem'; // 예: '12px', '1em' 등
    const statisticValueFontSize = isNarrow ? '1.2rem' : '1.4rem'; // 예: '20px', '1.5em' 등

    // --- 데이터 로딩 및 초기화 ---
    useEffect(() => {
        const fetchAssetsAndRunInitialBacktest = async () => {
            setLoading(true);
            try {
                const response = await apiClient.get('/strategy-lazy-portfolio/assets');
                const assets = response?.data?.assets || [];
                setAvailableAssets(assets);
                const initialWeights = { 'S&P500': 40,"Gold": 20,  'US10Y': 20, 'US01Y': 20 };
                setWeights(initialWeights);
                updateTotalWeight(initialWeights);
                await runBacktest(initialWeights, null, null, true);
            } catch (err) {
                setError('백엔드 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인하세요.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchAssetsAndRunInitialBacktest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runBacktest = useCallback(async (currentWeights, startDate, endDate, isInitialRun = false) => {
        setLoading(true);
        setError(null);
        setDateAdjustmentWarning(null); // 백테스트 실행 시 이전 안내 메시지 초기화
        const payload = {
            weights: currentWeights,
            start_date: startDate ? startDate.format('YYYY-MM-DD') : null,
            end_date: endDate ? endDate.format('YYYY-MM-DD') : null,
        };
        try {
            const response = await apiClient.post('/strategy-lazy-portfolio/backtest', payload);
            if (response.data && !response.data.error) {
                setResults(response.data);
                const newStartDate = dayjs(response.data.startDate);
                const newEndDate = dayjs(response.data.endDate);

                // 사용자가 요청한 날짜와 실제 백테스트 날짜가 다른 경우 안내 메시지 설정
                if (!isInitialRun) {
                    const requestedStartDate = startDate ? startDate.format('YYYY-MM-DD') : null;
                    const requestedEndDate = endDate ? endDate.format('YYYY-MM-DD') : null;
                    const actualStartDate = response.data.startDate;
                    const actualEndDate = response.data.endDate;

                    if (requestedStartDate !== actualStartDate || requestedEndDate !== actualEndDate) {
                        let baseMessage = `백테스트 기간이 ${actualStartDate} ~ ${actualEndDate}(으)로 자동 조정되었습니다.`;
                        const limitingAssets = response.data.limiting_assets;
                        let reasonMessage = "";
            
                        if (limitingAssets?.start?.length > 0) {
                            const startCulprits = limitingAssets.start.map(a => ASSET_NAMES_KO[a] || a).join(', ');
                            reasonMessage = ` ['${startCulprits}'의 데이터 범위에서 벗어났음]`;
                        }
                        setDateAdjustmentWarning(baseMessage + reasonMessage);
                    }
                }

                if (isInitialRun) {
                    setMinAvailableDate(newStartDate);
                    setMaxAvailableDate(newEndDate);
                }
                setBacktestStartDate(newStartDate);
                setBacktestEndDate(newEndDate);
            } else {
                throw new Error(response.data?.detail || '백테스트 데이터 분석 중 오류가 발생했습니다.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.detail || err.message || '백테스팅 중 오류가 발생했습니다.';
            setError(errorMessage);
            setResults(null);
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- 가중치 계산 및 이벤트 핸들러 ---
    const updateTotalWeight = (newWeights) => {
        const sum = Object.values(newWeights).reduce((acc, w) => acc + (w || 0), 0);
        setTotalWeight(sum);
    };
    const handleWeightChange = (asset, value) => {
        const newWeights = { ...weights, [asset]: value };
        setWeights(newWeights);
        updateTotalWeight(newWeights);
    };
    const handleAddAsset = (asset) => {
        if (!selectedAssets.includes(asset)) {
            setSelectedAssets([...selectedAssets, asset]);
            const newWeights = { ...weights, [asset]: 0 };
            setWeights(newWeights);
            updateTotalWeight(newWeights);
        }
    };
    const handleRemoveAsset = (assetToRemove) => {
        setSelectedAssets(selectedAssets.filter(asset => asset !== assetToRemove));
        const newWeights = { ...weights };
        delete newWeights[assetToRemove];
        setWeights(newWeights);
        updateTotalWeight(newWeights);
    };
    const handleRunBacktest = () => {
        if (totalWeight !== 100) {
            setError('총 비중은 정확히 100%가 되어야 합니다.');
            return;
        }
        setError(null);
        runBacktest(weights, backtestStartDate, backtestEndDate);
    };

    // --- 차트 옵션 생성 함수들 ---
    const getChartOption = useCallback((chartType) => {
        const datasets = (chartType === 'cumulative'
            ? results?.cumulativeReturnChartData?.datasets
            : results?.drawdownChartData?.datasets) || [];
        const labels = (chartType === 'cumulative'
            ? results?.cumulativeReturnChartData?.labels
            : results?.drawdownChartData?.labels) || [];

        const series = datasets.map(ds => ({
            name: ASSET_NAMES_KO[ds.label] || ds.label,
            type: 'line',
            data: labels.map((label, index) => [label, chartType === 'cumulative' ? (ds.data[index] - 1) * 100 : ds.data[index]]),
            showSymbol: false, smooth: false,
            lineStyle: { width: ds.label === 'My_Portfolio' ? 3 : 1.5, color: CHART_COLORS[ds.label] || '#5470C6' },
            itemStyle: { color: CHART_COLORS[ds.label] || '#5470C6' },
            ...(chartType === 'drawdown' && { areaStyle: { color: CHART_COLORS.Drawdown, opacity: 0.5 } })
        }));

        return {
            series: series, xAxis: { type: 'time' },
            yAxis: {
                type: 'value',
                // scale: true,
                log: true,
                axisLabel: { formatter: (value) => `${value.toFixed(0)}%` },
                axisLine: { show: true },
                position: 'right',
            },
            legend: { data: series.map(s => s.name), top: '10rem' },
            tooltip: { trigger: 'axis', valueFormatter: (value) => value != null ? `${value.toFixed(1)}%` : 'N/A' },
            grid: { top: '70rem', bottom: '10%', left: '20rem', right: '10rem', containLabel: true },
            dataZoom: [],
        };
    }, [results]);

    const getRollingCagrChartOption = useCallback(() => {
        if (!selectedCagrPeriod) return {};
        const chartData = results?.rollingCagrChartData?.[`${selectedCagrPeriod}-Year rolling CAGR`];
        const datasets = chartData?.datasets || [];
        const labels = chartData?.labels || [];

        const series = datasets.map(ds => ({
            name: ASSET_NAMES_KO[ds.label] || ds.label, type: 'line',
            data: labels.map((label, index) => [label, ds.data[index] * 100]),
            showSymbol: false, smooth: false,
            lineStyle: { width: ds.label === 'My_Portfolio' ? 3 : 1.5, color: CHART_COLORS[ds.label] || '#5470C6' },
            itemStyle: { color: CHART_COLORS[ds.label] || '#5470C6' },
        }));
        
        return {
            series: series, xAxis: { type: 'time' },
            yAxis: { type: 'value', scale: true, position: 'right', axisLabel: { formatter: (value) => `${value.toFixed(2)}%` }, axisLine: { show: true } },
            legend: { data: series.map(s => s.name), top: '0rem' },
            tooltip: { trigger: 'axis', valueFormatter: (value) => value != null ? `${value.toFixed(2)}%` : 'N/A' },
            grid: { top: '70rem', bottom: '10%', left: '0rem', right: '0rem', containLabel: true  },
            dataZoom: [],
        };
    }, [results, selectedCagrPeriod]);

    const getRiskReturnChartOption = useCallback(() => {
        const scatterData = results?.riskReturnChartData || [];
        
        // 유효한 데이터만 필터링
        const validScatterData = scatterData.filter(d => d != null && isFinite(d.risk) && isFinite(d.return));
        if (validScatterData.length === 0) {
            return { series: [] };
        }

        // --- 수정된 부분 시작 ---
        // 1. 'My_Portfolio' 데이터를 찾아서 샤프 지수(기울기)를 직접 계산합니다.
        // 이렇게 하면 CML이 항상 포트폴리오의 점을 통과하게 됩니다.
        const portfolioData = validScatterData.find(d => d.name === 'My_Portfolio');
        const sharpeRatio = (portfolioData && portfolioData.risk > 0) 
            ? portfolioData.return / portfolioData.risk 
            : 0;
        // --- 수정된 부분 끝 ---

        // 2. 축 범위와 CML 끝점 계산
        const risks = validScatterData.map(d => d.risk);
        const returns = validScatterData.map(d => d.return);
        const xMax = Math.max(...risks) * 1.1;
        const yMax = Math.max(...returns) * 1.2;
        const yMin = Math.min(0, ...returns) * 1.2;
        // 직접 계산한 샤프 지수를 사용하여 CML 끝점을 다시 계산합니다.
        const cmlEndPoint = [xMax, sharpeRatio * xMax];

        // 3. 점(Scatter) 시리즈 생성
        const scatterSeries = validScatterData.map(d => ({
            name: ASSET_NAMES_KO[d.name] || d.name,
            type: 'scatter',
            symbolSize: d.name === 'My_Portfolio' ? 20 : 15,
            data: [[d.risk, d.return]],
            itemStyle: { color: CHART_COLORS[d.name] || '#5470C6' },
            label: { show: true, formatter: '{b}', position: 'top' },
            z: 3 // z-index: 점을 가장 위로
        }));
        
        // 4. CML 점선 시리즈 생성
        const cmlLineSeries = {
            name: '내 포트폴리오 CML',
            type: 'line',
            smooth: false,
            showSymbol: false,
            data: [[0, 0], cmlEndPoint],
            lineStyle: { type: 'dashed', color: CHART_COLORS['My_Portfolio'], width: 2 },
            z: 2 // z-index: 선을 중간에
        };

        // 5. 음영 영역 시리즈 생성
        const superiorAreaSeries = {
            name: '우위 영역',
            type: 'line',
            data: [[0, 0], cmlEndPoint, [xMax, yMax], [0, yMax]],
            lineStyle: { width: 0 },
            areaStyle: { color: 'rgba(40, 167, 69, 0.1)' },
            smooth: false,
            silent: true,
            z: 1 // z-index: 영역을 가장 뒤로
        };
        const inferiorAreaSeries = {
            name: '열위 영역',
            type: 'line',
            data: [[0, 0], cmlEndPoint, [xMax, yMin], [0, yMin]],
            lineStyle: { width: 0 },
            areaStyle: { color: 'rgba(220, 53, 69, 0.1)' },
            smooth: false,
            silent: true,
            z: 1
        };

        const series = [superiorAreaSeries, inferiorAreaSeries, cmlLineSeries, ...scatterSeries];

        return {
            series: series,
            xAxis: {
                type: 'value', name: '리스크 (연 변동성)', nameLocation: 'middle', nameGap: 30,
                min: 0, max: xMax,
                axisLabel: { formatter: (value) => `${value.toFixed(1)}%` },
            },
            yAxis: {
                type: 'value', name: '초과 수익률', nameLocation: 'middle', nameGap: 50,
                min: yMin, max: yMax,
                axisLabel: { formatter: (value) => `${value.toFixed(1)}%` }, axisLine: { show: true }
            },
            legend: { data: validScatterData.map(d => ASSET_NAMES_KO[d.name] || d.name), top: '0rem' },
            tooltip: {
                trigger: 'item',
                formatter: (params) => {
                    if (params.seriesType !== 'scatter') return null;
                    const tooltipPortfolioData = scatterData.find(d => (ASSET_NAMES_KO[d.name] || d.name) === params.seriesName);
                    if (!tooltipPortfolioData) return null;
                    let tooltipHtml = `<b>${params.seriesName}</b><br/>` +
                                   `리스크: ${params.value[0].toFixed(2)}%<br/>` +
                                   `초과 수익률: ${params.value[1].toFixed(2)}%`;
                    if (tooltipPortfolioData.weights && Object.keys(tooltipPortfolioData.weights).length > 0) {
                        tooltipHtml += '<br/><div style="border-top: 1px solid #ccc; margin: 0.3125rem 0;"></div><b>포트폴리오 구성:</b><br/>';
                        for (const [asset, weight] of Object.entries(tooltipPortfolioData.weights)) {
                            if (weight > 0) {
                                tooltipHtml += `${ASSET_NAMES_KO[asset] || asset}: ${weight.toFixed(0)}%<br/>`;
                            }
                        }
                    }
                    return tooltipHtml;
                }
            },
            grid: { top: '70rem', bottom: '15%', left: '30rem', right: '20rem', containLabel: true },
            dataZoom: [],
        };
    }, [results]);

    // --- 나머지 컴포넌트 코드 ---
    const annualPerformanceColumns = [
        { title: '연도', dataIndex: 'Year', key: 'year', align: 'center', fixed: 'left' },
        { title: '수익률 ($)', dataIndex: 'Return', key: 'return', align: 'right', render: (text) => { const value = parseFloat(text); const color = value > 0 ? '#28a745' : value < 0 ? '#dc3545' : 'black'; return <span style={{ color: color, fontWeight: 'bold' }}>{value.toFixed(2)}%</span>; } },
        { title: '연간 MDD ($)', dataIndex: 'Annual_MDD', key: 'annual_mdd', align: 'right', render: (text) => <span style={{ color: '#dc3545', fontWeight: 'bold'  }}>{parseFloat(text).toFixed(2)}%</span> },
        {
            title: '수익률 (\u20A9)',
            dataIndex: 'Return_in_KRW',
            key: 'return_in_KRW',
            align: 'right',
            render: (text) => {
                const value = parseFloat(text);
                if (isNaN(value) || value === 0) return '-';
                const color = value > 0 ? '#28a745' : value < 0 ? '#dc3545' : 'black';
                return <span style={{ color: color, fontWeight: 'bold' }}>{value.toFixed(2)}%</span>;
            }
        },
        {
            title: '연간 MDD (\u20A9)',
            dataIndex: 'Annual_MDD_in_KRW',
            key: 'annual_mdd_in_KRW',
            align: 'right',
            render: (text) => {
                const value = parseFloat(text);
                if (isNaN(value) || value === 0) return '-';
                const color = value > 0 ? '#28a745' : value < 0 ? '#dc3545' : 'black';
                return <span style={{ color: color, fontWeight: 'bold' }}>{value.toFixed(2)}%</span>;
            }
        },


    ];

    const unselectedAssets = availableAssets.filter(asset => !selectedAssets.includes(asset));

    const sortedUnselectedAssets = useMemo(() =>
        [...unselectedAssets].sort((a, b) => {
            const nameA = ASSET_NAMES_KO[a] || a;
            const nameB = ASSET_NAMES_KO[b] || b;
            return nameA.localeCompare(nameB);
        }), [unselectedAssets]);

    const sortedAnnualReturns = useMemo(() => {
        if (!results?.annualReturnsTable) return [];
        return [...results.annualReturnsTable].sort((a, b) => b.Year - a.Year);
    }, [results]);

    const availableCagrPeriods = useMemo(() => {
        if (!results?.rollingCagrChartData) return [];
        return Object.keys(results.rollingCagrChartData)
            .map(key => key.split('-')[0])
            .sort((a, b) => parseInt(a) - parseInt(b));
    }, [results]);

    useEffect(() => {
        if (availableCagrPeriods.length > 0 && !availableCagrPeriods.includes(selectedCagrPeriod)) {
            setSelectedCagrPeriod(availableCagrPeriods[0]);
        }
    }, [availableCagrPeriods, selectedCagrPeriod]);


    return (
        <div className="content-box p-4 sm:p-8" >
            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>정적 자산 배분 포트폴리오 백테스터</Title>

            {/* --- 수정된 부분 시작 --- */}
            <Collapse ghost style={{ marginTop: '0.3125rem', marginBottom: '0.3125rem' }}>
                <Panel header={<Text strong style={{ color: '#1890ff', fontSize: '1rem' }}>전략 설명 보기 (클릭!)</Text>} key="1">
                    <Paragraph style={{ marginLeft: "0.3125rem", marginRight: "0.3125rem", textalign: 'left', wordBreak: 'keep-all' }}>
                        <Text strong>정적 자산 배분</Text>, 일명 <Text strong>'게으른 포트폴리오(Lazy Portfolio)'</Text>는 시장의 단기적인 움직임을 예측하려는 노력을 최소화하고, 미리 정해둔 자산 배분 원칙을 장기간 고수하는 투자 전략입니다.
                        일반적으로 정적 자산 배분 전략에선 주식과 채권, 또는 금이나 원자재처럼 장기적으로 상관관계가 낮은 자산들을 조합합니다.
                        그리고 정기적인 <Text strong>'리밸런싱(Rebalancing)'</Text>을 통해 초기 설정 비중을 유지함으로써, 가격이 오른 자산의 이익을 자연스럽게 실현(고점 매도)하고 가격이 떨어진 자산을 저렴하게 매수(저점 매수)하는 과정을 자동화하여 포트폴리오의 안정성을 유지합니다.
                    </Paragraph>
                    <Paragraph style={{ marginLeft: "0.3125rem", marginRight: "0.3125rem", textalign: 'left', wordBreak: 'keep-all' }}>
                        이러한 자산 배분 전략의 핵심은 단순히 높은 수익률을 좇는 것이 아니라, 감내해야 하는 <Text strong style={{ color: '#1890ff' }}>"변동성 대비 수익률"</Text>을 극대화하는 것입니다.
                        이 때문에 변동성 대비 성과를 측정하는 <Text strong>샤프 지수 (Sharpe Ratio)</Text>와 <Text strong>소르티노 지수 (Sortino Ratio)</Text>가 포트폴리오를 평가하는 핵심 지표로 활용됩니다.
                    </Paragraph>
                    <Paragraph style={{ marginLeft: "0.3125rem", marginRight: "0.3125rem", textalign: 'left', wordBreak: 'keep-all' }}>
                        단순 수익률은 높지만 변동성도 높아 샤프 지수가 낮은 포트폴리오 A가 있고, 수익률은 낮지만 변동성이 낮아 샤프 지수가 높은 포트폴리오 B가 있다고 가정해 보겠습니다.
                        이론적으로 포트폴리오 B에 레버리지를 사용하면 포트폴리오 A와 같은 위험 수준을 유지하면서 더 높은 기대 수익률을 갖도록 만들 수 있습니다.
                        따라서 이러한 과거 수익률 데이터에만 의존적인 기계적인 전략에서는 <Text strong style={{ color: '#1890ff' }}>"변동성 대비 수익률"</Text>이 높은 포트폴리오가 낮은 포트폴리오보다 <Text strong style={{ color: '#1890ff' }}>"우월"</Text>하다고 평가할 수 있습니다.
                    </Paragraph>
                    <Paragraph style={{ marginLeft: "0.3125rem", marginRight: "0.3125rem", textalign: 'left', wordBreak: 'keep-all' }}>
                        해당 벡테스트는 매월 1일 리밸런싱을 한다고 가정했습니다.
                    </Paragraph>
                </Panel>
            </Collapse>
            {/* --- 수정된 부분 끝 --- */}

            <Card style={{ marginTop: '1.25rem', marginBottom: '1.25rem', background: '#f8f9fa', textAlign: 'center' }} bodyStyle={{padding: '1rem'}}>
                <Text strong style={{ fontSize: '1.2em', color: '#2c3e50' }}>고(高) 샤프 지수 포트폴리오 예시</Text> <br/>
                <Text style={{ marginTop: '0.625rem', fontSize: '1.0em' }}>
                    S&P500(40%) &nbsp;•&nbsp; 금(20%) &nbsp;•&nbsp; 장기채(20%) &nbsp;•&nbsp; 현금성 단기채(20%)
                </Text>
            </Card>


            <Card title="자산 비중 및 기간 설정" style={{ marginTop: '1.5rem' }}>
                <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
                    <Col xs={24} sm={24} xl={10}  style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <SearchOutlined />
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="추가할 자산을 선택하세요"
                            onSelect={handleAddAsset}
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            value={null}
                        >
                            {sortedUnselectedAssets.map(asset => (
                                <Option key={asset} value={asset}>
                                    {ASSET_NAMES_KO[asset] || asset}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={[16, 24]}>
                    {selectedAssets.map(asset => (
                        <Col xs={24} sm={12} xl={6} key={asset}>
                            <Card
                                size="small"
                                title={ASSET_NAMES_KO[asset] || asset}
                                extra={<CloseCircleOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '1rem' }} onClick={() => handleRemoveAsset(asset)} />}
                            >
                                {/* --- 수정된 부분 시작 --- */}
                                <Row align="middle" gutter={8}>
                                    <Col>
                                        <Button
                                            size="small"
                                            icon={<MinusOutlined />}
                                            onClick={() => {
                                                const currentValue = weights[asset] || 0;
                                                const newValue = Math.max(0, currentValue - 5);
                                                handleWeightChange(asset, newValue);
                                            }}
                                            aria-label={`${ASSET_NAMES_KO[asset] || asset} 비중 감소`}
                                        />
                                    </Col>
                                    <Col flex="auto">
                                        <Slider
                                            min={0}
                                            max={100}
                                            step={5}
                                            value={weights[asset] || 0}
                                            onChange={(value) => handleWeightChange(asset, value)}
                                            style={{ margin: '0' }}
                                        />
                                    </Col>
                                    <Col>
                                        <Button
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => {
                                                const currentValue = weights[asset] || 0;
                                                const newValue = Math.min(100, currentValue + 5);
                                                handleWeightChange(asset, newValue);
                                            }}
                                            aria-label={`${ASSET_NAMES_KO[asset] || asset} 비중 증가`}
                                        />
                                    </Col>
                                    <Col flex="50px">
                                        <Text strong style={{ minWidth: '2.8125rem', textAlign: 'right', display: 'block' }}>
                                            {weights[asset] || 0}%
                                        </Text>
                                    </Col>
                                </Row>
                                {/* --- 수정된 부분 끝 --- */}
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop : "1.0rem"}} align="middle" justify="space-between" >
                    <Col xs={24} sm={24}>
                        {totalWeight !== 100 && (
                            <Alert 
                                style={{ fontSize: '0.9rem' }} 
                                        message={
                                                    <span>
                                                        자산 비중의 총합은 100%가 되어야 합니다. 
                                                        <br/>현재 비중 합계 :{' '} 
                                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                            {totalWeight}%
                                                        </span>
                                                    </span>
                                                    }
                                type="warning" 
                                showIcon 
                                />
                            )
                        }
                    </Col>
                    {/* <Col xs={24} sm={24} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Statistic title="현재 비중 합계" value={totalWeight} suffix="%" valueStyle={{ color: totalWeight !== 100 ? '#cf1322' : '#3f8600' }} />
                    </Col> */}
                </Row>
                <Divider style={{ margin: '1rem 0' }} />

                <Row gutter={[16, 16]} align="middle" justify={'space-between'}>
                    
                    {/* <Col xs={24} sm={24} md={4}>
                        <Text strong style={{wordBreak: 'keep-all',marginLeft:"0.25rem"}}><ClockCircleOutlined /> 백테스팅 기간 </Text>
                    </Col> */}
                    <Col xs={24} sm={24} md={10}  align="middle" style={{ display: 'flex', gap: '1rem' }}>
                        <ClockCircleOutlined />
                        <DatePicker 
                            placeholder="시작 날짜"
                            value={backtestStartDate ? dayjs(backtestStartDate) : null}
                            minDate={minAvailableDate ? dayjs(minAvailableDate) : null}
                            maxDate={backtestEndDate ? dayjs(backtestEndDate) : (maxAvailableDate ? dayjs(maxAvailableDate) : null)}
                            onChange={(date) => setBacktestStartDate(date)}
                            disabled={!minAvailableDate || loading}
                            style={{ width: '100%' }}
                            allowClear={false}
                        />
                        <ArrowRightOutlined />
                        <DatePicker 
                            placeholder="종료 날짜"
                            value={backtestEndDate ? dayjs(backtestEndDate) : null}
                            minDate={backtestStartDate ? dayjs(backtestStartDate) : (minAvailableDate ? dayjs(minAvailableDate) : null)}
                            maxDate={maxAvailableDate ? dayjs(maxAvailableDate) : null}
                            onChange={(date) => setBacktestEndDate(date)}
                            disabled={!minAvailableDate || loading}
                            style={{ width: '100%' }}
                            allowClear={false}
                        />
                    </Col>

                    <Col xs={24} sm={24} md={4}>
                        <Button 
                            type="primary" onClick={handleRunBacktest} block disabled={loading || totalWeight !== 100}
                            // style={{ width: isNarrow ? '100%' : '50%' }}
                        >백테스트 실행</Button>

                    </Col>
                </Row>
                {error && <Alert message={error} type="error" showIcon style={{ marginTop: '1rem' }} />}
                {dateAdjustmentWarning && <Alert message={dateAdjustmentWarning} type="info" showIcon style={{ marginTop: '1rem' }} />}

            </Card>

            <div style={{ marginTop: '1.5rem' }}>
                {loading && <Loading />}
                {!loading && results && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card>
                            <Row gutter={[16, 24]} justify="center">
                                {(results?.performanceMetrics || []).map(metric => (
                                    <Col key={metric.Metric} xs={12} sm={6} md={4} style={{ textAlign: 'center' }}>
                                        <Statistic 
                                        title={metric.Metric} titleStyle={{ fontSize: statisticTitleFontSize, color: '#555' }}
                                        value={metric.Value} valueStyle={{fontSize: statisticValueFontSize }}
            
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </Card>

                        <Card style={{ padding: '0rem'}} bodyStyle={{ padding: '1.0rem' }}>
                            <Tabs defaultActiveKey="1">
                                <TabPane tab="누적 수익률" key="1">
                                    <Title level={4} style={{ textAlign: 'center', marginTop: '1rem' }}>자산/포트폴리오 누적 수익률</Title>
                                    <ReactECharts option={getChartOption('cumulative')} style={{ height: '25rem', padding: '0.0rem' }} notMerge={true} />
                                </TabPane>
                                <TabPane tab="리스크/수익 분석" key="2">
                                    <div style={{ textAlign: 'center', margin: '1rem auto 1.25rem auto'}}>
                                        <Title level={4}>포트폴리오 리스크/수익 분석</Title>
                                        <Paragraph type="secondary" style={{ margin: 'auto', maxWidth: '37.5rem' }}>
                                            Y축의 <b>초과 수익률</b>은 포트폴리오의 수익률에서 무위험 수익률 (미국채 1년)을 차감한 값입니다.<br />
                                            점선은 현재 포트폴리오의 샤프 지수을 나타내며, 점선 위의 <span style={{color: '#28a745', fontWeight: 'bold'}}> (녹색 영역)</span>에 위치한 포트폴리오가
                                            더 <span style={{color: '#28a745', fontWeight: 'bold'}}>효율적인</span> 포트폴리오입니다.
                                        </Paragraph>

                                    </div>
                                    <ReactECharts option={getRiskReturnChartOption()} style={{ height: '25rem', padding: '0.0rem' }} notMerge={true} />
                                </TabPane>
                                <TabPane tab="최대 낙폭 (MDD)" key="3">
                                    <Title level={4} style={{ textAlign: 'center', marginTop: '1rem' }}>포트폴리오 최대 낙폭 (MDD)</Title>
                                    <ReactECharts option={getChartOption('drawdown')} style={{ height: '25rem', padding: '0.0rem' }} notMerge={true} />
                                </TabPane>
                                <TabPane tab="Rolling CAGR" key="4">
                                   <Title level={4} style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '1.0rem' }}>
                                        <Space>
                                            Rolling CAGR
                                            <Select
                                                value={selectedCagrPeriod}
                                                onChange={(value) => setSelectedCagrPeriod(value)}
                                                style={{width: '7.5rem'}}
                                                disabled={availableCagrPeriods.length === 0}
                                            >
                                                {availableCagrPeriods.map(period => (
                                                    <Option key={period} value={period}>{`${period}-Year`}</Option>
                                                ))}
                                            </Select>
                                        </Space>
                                    </Title>
                                    <ReactECharts option={getRollingCagrChartOption()} style={{ height: '25rem', padding: '0.0rem' }} notMerge={true} />
                                </TabPane>
                                <TabPane tab="연도별 성과" key="5">
                                     <Title level={4} style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '1.0rem' }}>포트폴리오 연간 수익률</Title>
                                    <Table
                                        dataSource={sortedAnnualReturns}
                                        columns={annualPerformanceColumns}
                                        rowKey="Year"
                                        pagination={{ 
                                                defaultPageSize: 10,
                                                showSizeChanger: true,
                                                pageSizeOptions: ['10', '20', '50', '100']
                                            }}
                                        size="small"
                                        scroll={{ x: 'max-content' }}
                                    />
                                </TabPane>
                            </Tabs>
                        </Card>
                    </Space>
                )}
            </div>
        </div>
    );
};

export default BacktestLazyPortfolioPage;